import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { RefreshCw, Home, Compass, LifeBuoy } from "lucide-react";
import { ErrorScreen } from "@/components/system/ErrorScreen";
import { logger } from "@/lib/observability";
import { captureException } from "@/lib/sentry";


interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Optional callback for error reporting (e.g., Sentry, LogRocket) */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Component name for better error tracking */
  componentName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
}

/**
 * Error Boundary component
 * Catches React errors and displays fallback UI
 * Supports optional error reporting callback for observability
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    errorId: null,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    // Generate unique error ID for tracking
    const errorId = `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return { hasError: true, error, errorId };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, componentName } = this.props;

    // P8.1 — logger + optional Sentry + ops_incidents fan-in (prod)
    const errorId = captureException(error, {
      componentName,
      componentStack: errorInfo.componentStack,
      errorId: this.state.errorId,
    });

    this.setState({ errorInfo, errorId });

    // Call external error reporter if provided
    if (onError) {
      try {
        onError(error, errorInfo);
      } catch (reportingError) {
        logger.error('Error reporting callback failed', {
          originalError: error.message,
          reportingError: reportingError instanceof Error ? reportingError.message : 'Unknown',
        });
      }
    }
  }

  private handleGoHome = () => {
    window.location.href = "/";
  };

  private handleBrowse = () => {
    window.location.href = "/learn";
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, errorId: null });
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isDev = import.meta.env.DEV;

      return (
        <ErrorScreen
          code="500"
          eyebrow="Unexpected error"
          title="Something broke mid-match"
          description="We hit an unexpected error rendering this screen. Your progress and data are safe — head back to a known route, or reload to try again."
          actions={
            <>
              <Button
                size="lg"
                onClick={this.handleGoHome}
                className="min-h-12 w-full gap-2 sm:w-auto"
              >
                <Home className="h-5 w-5" />
                Back to homepage
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={this.handleBrowse}
                className="min-h-12 w-full gap-2 sm:w-auto"
              >
                <Compass className="h-5 w-5" />
                Browse pathways
              </Button>
            </>
          }
          links={[
            {
              label: "Reload page",
              description: "Try loading this screen again",
              href: window.location.href,
              icon: <RefreshCw className="h-4 w-4" />,
            },
            {
              label: "Trust & support",
              description: "Report this error to our team",
              href: "/trust",
              icon: <LifeBuoy className="h-4 w-4" />,
            },
          ]}
          detail={
            <>
              {this.state.errorId && (
                <p className="text-center text-xs font-mono text-muted-foreground">
                  Error ID: {this.state.errorId}
                </p>
              )}
              {isDev && this.state.error && (
                <Card className="mt-4 border-destructive/20 bg-destructive/5 p-4 text-left">
                  <p className="mb-2 break-words font-mono text-sm text-destructive">
                    {this.state.error.toString()}
                  </p>
                  {this.state.errorInfo && (
                    <details className="mt-2 text-xs text-muted-foreground">
                      <summary className="cursor-pointer transition-colors hover:text-foreground">
                        Stack trace
                      </summary>
                      <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap text-xs">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </Card>
              )}
            </>
          }
        />
      );
    }


    return this.props.children;
  }
}
